import { useState, useEffect } from 'react';
import { RefreshCcw, Save, Layers } from 'lucide-react';
import { toast } from 'react-toastify';
import { Card, MediaUpload } from '@components/ui';
import { mockCompanies } from '@mocks/firmalar';
import { mockIndividuals } from '@mocks/musteriler';

export default function CekModal({
    isOpen,
    onClose,
    onSubmit,
    selectedCheck,
    isEdit,
    banks,
    checkStatuses,
    checkTypes
}) {
    // Form State
    const [isMultiCheck, setIsMultiCheck] = useState(false);
    const [checkCount, setCheckCount] = useState(1);

    const [formData, setFormData] = useState({
        type: 'alinan',
        checkNo: '', // Çoklu çek ise başlangıç numarası veya ön ek olabilir
        amount: '',
        dueDate: '',
        issueDate: '',
        bank: '',
        status: 'bekliyor',
        customer: '',   // Müşteri adı
        recipient: '',  // Tedarikçi/Taşeron adı
        description: '',
        note: '',
        document_url: null,
        multiChecks: [] // [{ amount: '', dueDate: '' }]
    });

    useEffect(() => {
        if (selectedCheck) {
            setIsMultiCheck(false); // Düzenleme işlemi daima tekildir
            setFormData({
                ...selectedCheck,
                customer: selectedCheck.customer || '',
                recipient: selectedCheck.recipient || '',
                document_url: selectedCheck.document_url || null,
                multiChecks: []
            });
        } else {
            setIsMultiCheck(false);
            setCheckCount(1);
            setFormData({
                type: 'alinan',
                checkNo: '',
                amount: '',
                dueDate: '',
                issueDate: new Date().toISOString().split('T')[0],
                bank: '',
                status: 'bekliyor',
                customer: '',
                recipient: '',
                description: '',
                note: '',
                document_url: null,
                multiChecks: [{ amount: '', dueDate: '' }]
            });
        }
    }, [selectedCheck]);

    if (!isOpen) return null;

    const handleMultiCheckChange = (index, field, value) => {
        const updated = [...formData.multiChecks];
        updated[index][field] = value;
        setFormData({ ...formData, multiChecks: updated });
    };

    const handleCheckCountChange = (e) => {
        const count = parseInt(e.target.value) || 1;
        setCheckCount(count);

        let newMulti = [...formData.multiChecks];
        if (count > newMulti.length) {
            const diff = count - newMulti.length;
            for (let i = 0; i < diff; i++) newMulti.push({ amount: formData.amount || '', dueDate: '' });
        } else {
            newMulti = newMulti.slice(0, count);
        }
        setFormData({ ...formData, multiChecks: newMulti });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Çoklu çek modundaysa form validation
        if (!isEdit && isMultiCheck) {
            const invalid = formData.multiChecks.find(c => !c.amount || !c.dueDate);
            if (invalid) {
                toast.error('Seri çeklerin tüm tutar ve vade tarihlerini doldurun');
                return;
            }
        }

        onSubmit({ ...formData, isMultiCheck, checkCount });
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <Card padding="lg" className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-text-primary">
                        {isEdit ? 'Çek Detayı / Düzenle' : 'Yeni Çek Ekle'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-bg-hover rounded-lg transition-colors text-text-tertiary"
                    >
                        ✕
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Temel Bilgiler */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">Çek Türü</label>
                            <select
                                value={formData.type}
                                onChange={e => setFormData({ ...formData, type: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg border border-border-light bg-bg-primary text-text-primary focus:outline-none focus:border-primary disabled:opacity-50"
                                disabled={isEdit} // Edit modunda tür değişmez
                            >
                                <option value="alinan">Alınan Çek (Müşteriden)</option>
                                <option value="verilen">Verilen Çek (Tedarikçiye)</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">Durum</label>
                            <select
                                value={formData.status}
                                onChange={e => setFormData({ ...formData, status: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg border border-border-light bg-bg-primary text-text-primary focus:outline-none focus:border-primary"
                            >
                                {Object.entries(checkStatuses).map(([key, { label }]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">Çek No</label>
                            <input
                                type="text"
                                required
                                value={formData.checkNo}
                                onChange={e => setFormData({ ...formData, checkNo: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg border border-border-light bg-bg-primary text-text-primary focus:outline-none focus:border-primary"
                                placeholder="Örn: 12345678"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">Banka</label>
                            <select
                                required
                                value={formData.bank}
                                onChange={e => setFormData({ ...formData, bank: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg border border-border-light bg-bg-primary text-text-primary focus:outline-none focus:border-primary"
                            >
                                <option value="">Seçiniz...</option>
                                {banks.map(bank => (
                                    <option key={bank.key} value={bank.key}>{bank.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Kişi / Kurum (Türe göre değişir) */}
                        {formData.type === 'alinan' ? (
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-text-secondary mb-1">Kimden Alındı (Müşteri)</label>
                                <select
                                    required
                                    value={formData.customer}
                                    onChange={e => setFormData({ ...formData, customer: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg border border-border-light bg-bg-primary text-text-primary focus:outline-none focus:border-primary"
                                >
                                    <option value="">Firma / Müşteri Seçin...</option>
                                    {mockIndividuals.slice(0, 10).map(ind => (
                                        <option key={ind.id} value={ind.name}>{ind.name} (T.C: {ind.tcNo.substring(0, 6)}...)</option>
                                    ))}
                                    <option value="Diger">Diğer / Listede Yok</option>
                                </select>
                                {selectedCheck?.linked_sale_installment_id && (
                                    <p className="text-xs text-success mt-1">✓ Bu çek, bir daire satışı taksidine bağlıdır.</p>
                                )}
                            </div>
                        ) : (
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-text-secondary mb-1">Kime Verildi (Tedarikçi/Taşeron)</label>
                                <select
                                    required
                                    value={formData.recipient}
                                    onChange={e => setFormData({ ...formData, recipient: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg border border-border-light bg-bg-primary text-text-primary focus:outline-none focus:border-primary"
                                >
                                    <option value="">Tedarikçi / Kurum Seçin...</option>
                                    {mockCompanies.map(comp => (
                                        <option key={comp.id} value={comp.name}>{comp.name} ({comp.typeLabel})</option>
                                    ))}
                                </select>
                                {selectedCheck?.linked_scheduled_payment_id && (
                                    <p className="text-xs text-warning mt-1">✓ Bu çek, planlı bir gider ödemesine bağlıdır.</p>
                                )}
                            </div>
                        )}

                        {/* Seri Çek Toggle Alanı (Sadece Yeni Eklerken) */}
                        {!isEdit && (
                            <div className="md:col-span-2 flex items-center gap-4 bg-bg-secondary p-4 rounded-xl border border-border-light mt-2">
                                <div className="flex items-center gap-3 flex-1">
                                    <div className={`p-2 rounded-lg ${isMultiCheck ? 'bg-primary/10 text-primary' : 'bg-neutral-200 text-neutral-500'}`}>
                                        <Layers size={20} />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-text-primary text-sm">Seri (Taksitli) Çek Girişi</h4>
                                        <p className="text-xs text-text-tertiary">Tek seferde birbirini izleyen vadeli çekler ekleyin.</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={isMultiCheck}
                                            onChange={(e) => {
                                                setIsMultiCheck(e.target.checked);
                                                if (e.target.checked && formData.multiChecks.length === 0) {
                                                    setFormData({ ...formData, multiChecks: [{ amount: formData.amount, dueDate: formData.dueDate }] });
                                                }
                                            }}
                                        />
                                        <div className="w-11 h-6 bg-neutral-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>
                            </div>
                        )}

                        {/* Tutar ve Vade (Tekli ise standart görünecek) */}
                        {!isMultiCheck && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-text-secondary mb-1">Tutar (₺)</label>
                                    <input
                                        type="number"
                                        required
                                        value={formData.amount}
                                        onChange={e => setFormData({ ...formData, amount: e.target.value })}
                                        className="w-full px-3 py-2 rounded-lg border border-border-light bg-bg-primary text-text-primary focus:outline-none focus:border-primary"
                                        placeholder="0.00"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-text-secondary mb-1">Vade Tarihi</label>
                                    <input
                                        type="date"
                                        required
                                        value={formData.dueDate}
                                        onChange={e => setFormData({ ...formData, dueDate: e.target.value })}
                                        className="w-full px-3 py-2 rounded-lg border border-border-light bg-bg-primary text-text-primary focus:outline-none focus:border-primary"
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    {/* Çoklu Çek Tablosu (Sadece isMultiCheck Açıkken) */}
                    {isMultiCheck && !isEdit && (
                        <div className="bg-bg-tertiary p-4 rounded-xl border border-border-light space-y-4 shadow-inner">
                            <div className="flex items-center justify-between">
                                <h4 className="font-semibold text-text-primary text-sm flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-primary"></span>
                                    Vade ve Tutar Dağılımı
                                </h4>
                                <div className="flex items-center gap-2">
                                    <label className="text-sm text-text-secondary">Yaprak Sayısı:</label>
                                    <input
                                        type="number"
                                        min="2" max="24"
                                        className="w-16 px-2 py-1 text-center rounded border border-border-light focus:outline-none focus:border-primary"
                                        value={checkCount}
                                        onChange={handleCheckCountChange}
                                    />
                                </div>
                            </div>

                            <div className="space-y-3 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
                                {formData.multiChecks.map((multiItem, index) => (
                                    <div key={index} className="flex items-center gap-3 bg-bg-primary p-3 rounded-lg border border-border-light">
                                        <div className="w-8 h-8 rounded-full bg-bg-secondary flex items-center justify-center text-sm font-bold text-text-secondary">
                                            {index + 1}
                                        </div>
                                        <div className="flex-1">
                                            <input
                                                type="number"
                                                required
                                                placeholder="Tutar (₺)"
                                                className="w-full px-3 py-1.5 text-sm rounded border border-border-light focus:outline-none focus:border-primary"
                                                value={multiItem.amount}
                                                onChange={(e) => handleMultiCheckChange(index, 'amount', e.target.value)}
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <input
                                                type="date"
                                                required
                                                className="w-full px-3 py-1.5 text-sm rounded border border-border-light focus:outline-none focus:border-primary"
                                                value={multiItem.dueDate}
                                                onChange={(e) => handleMultiCheckChange(index, 'dueDate', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="text-right text-xs text-text-tertiary pt-2 border-t border-border-light">
                                * Toplam Tutar: <span className="font-bold text-text-primary">
                                    {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(formData.multiChecks.reduce((a, b) => a + (Number(b.amount) || 0), 0))}
                                </span>
                            </div>
                        </div>
                    )}

                    <hr className="border-border-light" />

                    {/* Evrak Görseli (Dosya Yükleme Alanı) */}
                    <MediaUpload
                        mode="single"
                        accept="image"
                        label="Çek Görseli (Evrak)"
                        hint="Çekin önlü arkalı fotoğrafını yükleyin"
                        maxSizeMB={5}
                        compact
                        initialUrls={formData.document_url ? [formData.document_url] : []}
                        onFilesChange={(files) => setFormData(prev => ({
                            ...prev,
                            document_url: files.length > 0 ? files[0] : null
                        }))}
                    />

                    {/* ERP Ciro Aksiyonu Alanı */}
                    {isEdit && formData.type === 'alinan' && formData.status === 'bekliyor' && (
                        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 space-y-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h4 className="text-sm font-bold text-warning-dark">Ciro Et</h4>
                                    <p className="text-xs text-warning-dark/70">Müşteriden alınan bu çeki, nakde çevirmeden faturaya istinaden tedarikçiye ödeme olarak devret.</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, showCiroPrompt: !formData.showCiroPrompt })}
                                    className="px-3 py-1.5 bg-warning text-white font-medium text-xs rounded-lg hover:bg-warning-dark transition-colors flex items-center gap-1 shadow-sm whitespace-nowrap"
                                >
                                    <RefreshCcw size={14} />
                                    {formData.showCiroPrompt ? 'İptal' : 'Ciro Et'}
                                </button>
                            </div>

                            {formData.showCiroPrompt && (
                                <div className="pt-3 border-t border-warning/20">
                                    <label className="block text-xs font-medium text-warning-dark mb-1">
                                        Hangi Tedarikçiye / Firmaya Devrediyorsunuz?
                                    </label>
                                    <div className="flex gap-2">
                                        <select
                                            className="flex-1 px-3 py-2 rounded-lg border border-warning/30 bg-white text-sm focus:outline-none focus:border-warning"
                                            value={formData.ciroRecipient || ''}
                                            onChange={(e) => setFormData({ ...formData, ciroRecipient: e.target.value })}
                                        >
                                            <option value="">Firma / Tedarikçi Seçin...</option>
                                            {mockCompanies.map(comp => (
                                                <option key={comp.id} value={comp.name}>{comp.name} - {comp.specialty}</option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (!formData.ciroRecipient) return toast.error('Lütfen devredilecek firmayı seçiniz');
                                                toast.success(`Çek, ${formData.ciroRecipient} firmasına başarıyla ciro edildi.`);
                                                setFormData({ ...formData, showCiroPrompt: false, status: 'ciro_edildi', recipient: formData.ciroRecipient });
                                            }}
                                            className="px-4 py-2 bg-success text-white text-sm font-medium rounded-lg hover:bg-success-dark transition-colors"
                                        >
                                            Uygula
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}


                    <div className="flex justify-end gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-text-secondary bg-bg-tertiary rounded-lg hover:bg-bg-hover transition-colors"
                        >
                            İptal
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-2"
                        >
                            <Save size={18} />
                            {isEdit ? 'Güncelle' : 'Kaydet'}
                        </button>
                    </div>
                </form>
            </Card>
        </div>
    );
}
